import type {Dispatch, GetState, ThunkAction} from 'src/reducers';
import type {Query} from 'src/types/router';
import type {ApiLinkStats, LinkStats} from 'src/types/link-stats';
import type {AnalyticsQuery} from 'src/types/report';

import {thunkify as flow} from 'src/utils/thunks';
import {camel} from 'src/utils';
import pick from 'lodash/pick';

import {
  resolveAnalyticsQuery,
  resolveAnalyticsQueryV2,
  commonFilters,
  metricsFilters,
} from 'src/utils/analytics-query';
import {key, cached, fetching} from 'src/utils/redux';
import * as reduxApi from 'src/utils/redux-api';

import {
  checkExternalLinkLatestApiEnabled,
  ANALYTICS_API_V3,
} from 'src/utils/analytics-api-migration';


const {stringify} = JSON;

export const RECEIVE_AGENCY = 'linkStats/receiveAgency';
export const RECEIVE_WORKFLOW = 'linkStats/receiveWorkflow';
export const RECEIVE_EVENT = 'linkStats/receiveEvent';

type PaginatedLinkStats = {
  page: number;
  linkStats: ApiLinkStats;
};

export type AgencyLinkStatsAction = {
  type: 'linkStats/receiveAgency';
  payload: PaginatedLinkStats;
};
export type WorkflowLinkStatsAction = {
  type: 'linkStats/receiveWorkflow';
  payload: PaginatedLinkStats & {
    workflowId: string;
  };
};
export type EventLinkStatsAction = {
  type: 'linkStats/receiveEvent';
  payload: PaginatedLinkStats & {
    eventId: string;
  };
};
export type LinkStatsAction =
  | AgencyLinkStatsAction
  | WorkflowLinkStatsAction
  | EventLinkStatsAction;

export const receiveAgency = (
  linkStats: ApiLinkStats,
  page: number,
): AgencyLinkStatsAction => ({
  type: RECEIVE_AGENCY,
  payload: {
    linkStats,
    page,
  },
});

export const receiveWorkflow = (
  linkStats: ApiLinkStats,
  workflowId: string,
  page: number,
): WorkflowLinkStatsAction => ({
  type: RECEIVE_WORKFLOW,
  payload: {
    workflowId,
    linkStats,
    page,
  },
});

export const receiveEvent = (
  linkStats: ApiLinkStats,
  eventId: string,
  page: number,
): EventLinkStatsAction => ({
  type: RECEIVE_EVENT,
  payload: {
    eventId,
    linkStats,
    page,
  },
});

export const PAGINATION_LIMIT = 50;
function resolvePageQuery(query: Query, page: number) {
  return {...query, offset: page * PAGINATION_LIMIT, limit: PAGINATION_LIMIT};
}

export const allowedFilters: string[] = [...commonFilters, ...metricsFilters];

export function resolveLinkStatsQuery(
  query: Query,
  allowed: string[] = [...commonFilters, ...metricsFilters],
  isEnhancement: boolean = false,
): AnalyticsQuery {
  if (isEnhancement) {
    return resolveAnalyticsQueryV2(query, allowed);
  }
  return resolveAnalyticsQuery(query, allowed);
}

const querying =
  // @ts-expect-error - TS7006 - Parameter 'allowed' implicitly has an 'any' type.


    (paramIndex: number, allowed, isEnhancement = false) =>
    (func: any) =>
    // @ts-expect-error - TS7019 - Rest parameter 'args' implicitly has an 'any[]' type.
    (...args) => {
      args[paramIndex] = resolveLinkStatsQuery(
        args[paramIndex],
        allowed,
        isEnhancement,
      );
      return func(...args);
    };

export const getAgencyLinkStats: (
  query: Query,
  page: number,
  otherFilterQuery: Query,
) => ThunkAction<unknown> = flow(
  // @ts-expect-error - TS2554 - Expected 2-3 arguments, but got 1.
  querying(0),
  key((query, page) => `linkStats/agency:${page}`),
  cached((json, query, page = 0) => receiveAgency(camel(json), page), {
    hash: (query, page) => `linkStats/agency:${page}:${stringify(query)}`,
  }),
  fetching(),
)(
  (
    query: Query,
    page: number | null | undefined = 0,
    otherFilterQuery: Query,
  ) => {
    const resolved = resolveQuery(otherFilterQuery);
    const q = {
      ...query,
      ...resolved,
    } as const;
    // @ts-expect-error - TS2345 - Argument of type '{ readonly start_date?: string; readonly end_date?: string; readonly event_id: string | number | boolean; readonly workflow_id: string | number | boolean; readonly offset?: number; ... 8 more ...; readonly show_filter: string | ... 1 more ... | boolean; }' is not assignable to parameter of type 'Query'.
    const apiPayload = resolvePageQuery(q, page);
    return reduxApi.post(
      'sent-links',
      apiPayload,
      {},
      {
        apiPath: checkExternalLinkLatestApiEnabled()
          ? `${ANALYTICS_API_V3}/`
          : '/api/v1/analytics/',
      },
    );
  },
);

export const getMoreAgencyLinkStats =
  (query: Query): ThunkAction<unknown> =>
  (dispatch: Dispatch, getState: GetState) => {
    return dispatch(
      getAgencyLinkStats(query, getState().linkStats.agency.page + 1, query),
    ).catch((error) => {
      console.log('Error getting more agency link stats:', error);
      return null;
    });
  };

export const getWorkflowLinkStats: (
  arg1: string,
  arg2: Query,
  page: number,
  otherFilterQuery: Query,
) => ThunkAction<unknown> = flow(
  // @ts-expect-error - TS2554 - Expected 2-3 arguments, but got 1.
  querying(1),
  key((workflowId, query, page) => `linkStats/workflow:${workflowId}:${page}`),
  cached(
    (json, workflowId, query, page = 0) =>
      receiveWorkflow(camel(json), workflowId, page),
    {
      hash: (workflowId, query, page) =>
        `linkStats/workflow:${workflowId}:${page}:${stringify(query)}`,
    },
  ),
  fetching(),
)(
  (
    workflowId: string,
    query: Query,
    page: number | null | undefined = 0,
    otherFilterQuery: Query,
  ) => {
    const resolved = resolveQuery(otherFilterQuery);
    const q = {
      ...query,
      ...resolved,
    } as const;
    // @ts-expect-error - TS2345 - Argument of type '{ readonly start_date?: string; readonly end_date?: string; readonly event_id: string | number | boolean; readonly workflow_id: string | number | boolean; readonly offset?: number; ... 8 more ...; readonly show_filter: string | ... 1 more ... | boolean; }' is not assignable to parameter of type 'Query'.
    const apiPayload = resolvePageQuery(q, page);
    return reduxApi.post(
      `workflows/${workflowId}/sent-links`,
      apiPayload,
      {},
      {
        apiPath: checkExternalLinkLatestApiEnabled()
          ? `${ANALYTICS_API_V3}/`
          : '/api/v1/analytics/',
      },
    );
  },
);

export const getMoreWorkflowLinkStats =
  (workflowId: string, query: Query): ThunkAction<unknown> =>
  (dispatch: Dispatch, getState: GetState) =>
    dispatch(
      getWorkflowLinkStats(
        workflowId,
        query,
        getState().linkStats.workflows[workflowId].page + 1,
        query,
      ),
    );

export const getEventLinkStats: (
  workflowId: string,
  eventId: string,
  query: Query,
  page: number,
  otherFilterQuery: Query,
) => ThunkAction<unknown> = flow(
  querying(2, ['client', 'tracking_events']),
  key(
    (workflowId, eventId, query, page) =>
      `linkStats/workflow:${workflowId}:${eventId}:${page}`,
  ),
  cached(
    (json, workflowId, eventId, query, page = 0) =>
      receiveEvent(camel(json), eventId, page),
    {
      hash: (workflowId, eventId, query, page) =>
        `linkStats/workflow:${workflowId}:${eventId}:${page}:${stringify(
          query,
        )}`,
    },
  ),
  fetching(),
)(
  (
    workflowId: string,
    eventId: string,
    query: Query,
    page: number | null | undefined = 0,
    otherFilterQuery: Query,
  ) => {
    const resolved = resolveQuery(otherFilterQuery);
    const q = {
      ...query,
      ...resolved,
    } as const;
    // @ts-expect-error - TS2345 - Argument of type '{ readonly start_date?: string; readonly end_date?: string; readonly event_id: string | number | boolean; readonly workflow_id: string | number | boolean; readonly offset?: number; ... 8 more ...; readonly show_filter: string | ... 1 more ... | boolean; }' is not assignable to parameter of type 'Query'.
    const apiPayload = resolvePageQuery(q, page);
    return reduxApi.post(
      `workflows/${workflowId}/events/${eventId}/sent-links`,
      apiPayload,
      {},
      {
        apiPath: checkExternalLinkLatestApiEnabled()
          ? `${ANALYTICS_API_V3}/`
          : '/api/v1/analytics/',
      },
    );
  },
);

export const getMoreEventLinkStats =
  (workflowId: string, eventId: string, query: Query): ThunkAction<unknown> =>
  (dispatch: Dispatch, getState: GetState) =>
    dispatch(
      getEventLinkStats(
        workflowId,
        eventId,
        query,
        getState().linkStats.events[eventId].page + 1,
        query,
      ),
    );

export const getAgencyLinkStatsV2: (
  query: Query,
  page: number,
  otherFilterQuery: Query,
) => ThunkAction<unknown> = flow(
  querying(0, [...commonFilters, ...metricsFilters], true),
  key((query, page) => `linkStats/agency:${page}`),
  cached((json, query, page = 0) => receiveAgency(camel(json), page), {
    hash: (query, page) => `linkStats/agency:${page}:${stringify(query)}`,
  }),
  fetching(),
)(
  (
    query: Query,
    page: number | null | undefined = 0,
    otherFilterQuery: Query,
  ) => {
    const resolved = resolveQuery(otherFilterQuery);
    const q = {
      ...query,
      ...resolved,
    } as const;
    // @ts-expect-error - TS2345 - Argument of type '{ readonly start_date?: string; readonly end_date?: string; readonly event_id: string | number | boolean; readonly workflow_id: string | number | boolean; readonly offset?: number; ... 8 more ...; readonly show_filter: string | ... 1 more ... | boolean; }' is not assignable to parameter of type 'Query'.
    const apiPayload = resolvePageQuery(q, page);
    return reduxApi.post(
      'sent-links',
      apiPayload,
      {},
      {
        apiPath: checkExternalLinkLatestApiEnabled()
          ? `${ANALYTICS_API_V3}/`
          : '/api/v1/analytics/',
      },
    );
  },
);

export const getMoreAgencyLinkStatsV2 =
  (query: Query): ThunkAction<unknown> =>
  (dispatch: Dispatch, getState: GetState) => {
    return dispatch(
      getAgencyLinkStatsV2(query, getState().linkStats.agency.page + 1, query),
    ).catch((error) => {
      console.log('Error getting more agency link stats:', error);
      return null;
    });
  };

export const getWorkflowLinkStatsV2: (
  arg1: string,
  arg2: Query,
  page: number,
  otherFilterQuery: Query,
) => ThunkAction<unknown> = flow(
  querying(1, [...commonFilters, ...metricsFilters], true),
  key((workflowId, query, page) => `linkStats/workflow:${workflowId}:${page}`),
  cached(
    (json, workflowId, query, page = 0) =>
      receiveWorkflow(camel(json), workflowId, page),
    {
      hash: (workflowId, query, page) =>
        `linkStats/workflow:${workflowId}:${page}:${stringify(query)}`,
    },
  ),
  fetching(),
)(
  (
    workflowId: string,
    query: Query,
    page: number | null | undefined = 0,
    otherFilterQuery: Query,
  ) => {
    const resolved = resolveQuery(otherFilterQuery);
    const q = {
      ...query,
      ...resolved,
    } as const;
    // @ts-expect-error - TS2345 - Argument of type '{ readonly start_date?: string; readonly end_date?: string; readonly event_id: string | number | boolean; readonly workflow_id: string | number | boolean; readonly offset?: number; ... 8 more ...; readonly show_filter: string | ... 1 more ... | boolean; }' is not assignable to parameter of type 'Query'.
    const apiPayload = resolvePageQuery(q, page);
    return reduxApi.post(
      `workflows/${workflowId}/sent-links`,
      apiPayload,
      {},
      {
        apiPath: checkExternalLinkLatestApiEnabled()
          ? `${ANALYTICS_API_V3}/`
          : '/api/v1/analytics/',
      },
    );
  },
);

export const getMoreWorkflowLinkStatsV2 =
  (workflowId: string, query: Query): ThunkAction<unknown> =>
  (dispatch: Dispatch, getState: GetState) =>
    dispatch(
      getWorkflowLinkStatsV2(
        workflowId,
        query,
        getState().linkStats.workflows[workflowId].page + 1,
        query,
      ),
    );

export const getEventLinkStatsV2: (
  workflowId: string,
  eventId: string,
  query: Query,
  page: number,
  otherFilterQuery: Query,
) => ThunkAction<unknown> = flow(
  querying(2, ['client', 'tracking_events'], true),
  key(
    (workflowId, eventId, query, page) =>
      `linkStats/workflow:${workflowId}:${eventId}:${page}`,
  ),
  cached(
    (json, workflowId, eventId, query, page = 0) =>
      receiveEvent(camel(json), eventId, page),
    {
      hash: (workflowId, eventId, query, page) =>
        `linkStats/workflow:${workflowId}:${eventId}:${page}:${stringify(
          query,
        )}`,
    },
  ),
  fetching(),
)(
  (
    workflowId: string,
    eventId: string,
    query: Query,
    page: number | null | undefined = 0,
    otherFilterQuery: Query,
  ) => {
    const resolved = resolveQuery(otherFilterQuery);
    const q = {
      ...query,
      ...resolved,
    } as const;
    // @ts-expect-error - TS2345 - Argument of type '{ readonly start_date?: string; readonly end_date?: string; readonly event_id: string | number | boolean; readonly workflow_id: string | number | boolean; readonly offset?: number; ... 8 more ...; readonly show_filter: string | ... 1 more ... | boolean; }' is not assignable to parameter of type 'Query'.
    const apiPayload = resolvePageQuery(q, page);
    return reduxApi.post(
      `workflows/${workflowId}/events/${eventId}/sent-links`,
      apiPayload,
      {},
      {
        apiPath: checkExternalLinkLatestApiEnabled()
          ? `${ANALYTICS_API_V3}/`
          : '/api/v1/analytics/',
      },
    );
  },
);

export const getMoreEventLinkStatsV2 =
  (workflowId: string, eventId: string, query: Query): ThunkAction<unknown> =>
  (dispatch: Dispatch, getState: GetState) =>
    dispatch(
      getEventLinkStatsV2(
        workflowId,
        eventId,
        query,
        getState().linkStats.events[eventId].page + 1,
        query,
      ),
    );

function resolveQuery(query: Query) {
  // @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
  const resolvedQueryObject = resolveAnalyticsQueryV2(query);

  if (resolvedQueryObject['recipient_type']) {
    // @ts-expect-error - TS2322 - Type 'string | number | true' is not assignable to type 'string[] | undefined'.
    resolvedQueryObject.recipients = resolvedQueryObject['recipient_type'];
  }

  return {
    ...pick(resolvedQueryObject, [
      'start_date',
      'end_date',
      'workflow_id',
      'event_id',
      'offset',
      'limit',
      'message_goal_type',
      'show_filter',
      'result',
      'delivery_status',
      'entity_data_filter',
      'from_address',
      'event_type_filter',
      'recipients',
    ]),
  };
}
